import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BiVizDashboardResourcesSqlMap } from '../sql-maps/biviz-dashboard-resources-datasource-sql-map';

export interface DashboardResourcesByDateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class DashboardResourcesByDateDataLoaderFactory extends Factory<
    DataLoader,
    DashboardResourcesByDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'DashboardResourcesByDateDataLoaderFactory';

    getInstance(options: DashboardResourcesByDateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const resourcesSqlMap = BiVizDashboardResourcesSqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    resourcesSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const resourcesSQL =
                    BiVizDashboardResourcesSqlMap.sqlStatementsCompiler.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const usersdashActivity = BiVizDashboardUsersActivity;

                // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                const usersdashActivityDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        usersdashActivityDsColumns.missing
                    );

                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const allResources =
                    BiVizDashboardResourcesSqlMap.ALL_RESOURCES;
                const win = resourcesSQL.windowRunning('c');

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH dev AS
(
  SELECT *
    , CASE WHEN energy_inflow IS NULL AND energy_outflow IS NULL THEN NULL ELSE COALESCE(energy_inflow, 0) - COALESCE(energy_outflow, 0) END AS energy_balance
    , CASE WHEN gems_inflow IS NULL AND gems_outflow IS NULL THEN NULL ELSE COALESCE(gems_inflow, 0) - COALESCE(gems_outflow, 0) END AS gems_balance
    , CASE WHEN hints_inflow IS NULL AND hints_outflow IS NULL THEN NULL ELSE COALESCE(hints_inflow, 0) - COALESCE(hints_outflow, 0) END AS hints_balance
    , CASE WHEN coins_inflow IS NULL AND coins_outflow IS NULL THEN NULL ELSE COALESCE(coins_inflow, 0) - COALESCE(coins_outflow, 0) END AS coins_balance
    , CASE WHEN compasses_inflow IS NULL AND compasses_outflow IS NULL THEN NULL ELSE COALESCE(compasses_inflow, 0) - COALESCE(compasses_outflow, 0) END AS compasses_balance
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      resourcesSqlMap.dataset,
      resourcesSqlMap.tableId
  )}\` ds
  -- WHERE energy_inflow <= 1000000 OR energy_inflow IS NULL -- filter out cheaters
)

, resourcesAggBase AS
(
  SELECT 
    ds.*,
    ${resourcesSQL.splitSelect}
    ${resourcesSQL.datasourceSpecificSplitSelect}
    ${resourcesSQL.cohortSplitSelect}
  FROM dev ds
  ${resourcesSQL.join}
  ${resourcesSQL.where}
)

, resourcesAgg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.dateTrunc()} AS date
    ${allResources
        .flatMap((r) => [
            `, SUM(${r}_inflow) AS ${r}_inflow`,
            `, SUM(${r}_outflow) AS ${r}_outflow`,
            `, SUM(${r}_balance) AS ${r}_balance`,
        ])
        .join('\n    ')}

    ${allResources
        .flatMap((r) => [
            `, APPROX_QUANTILES(${r}_inflow, 100)[OFFSET(50)] AS median_${r}_inflow_per_active_user`,
            `, APPROX_QUANTILES(${r}_inflow, 100)[OFFSET(75)] AS p75_${r}_inflow_per_active_user`,
            `, APPROX_QUANTILES(${r}_inflow, 100)[OFFSET(90)] AS p90_${r}_inflow_per_active_user`,
            `, APPROX_QUANTILES(${r}_outflow, 100)[OFFSET(50)] AS median_${r}_outflow_per_active_user`,
            `, APPROX_QUANTILES(${r}_outflow, 100)[OFFSET(75)] AS p75_${r}_outflow_per_active_user`,
            `, APPROX_QUANTILES(${r}_outflow, 100)[OFFSET(90)] AS p90_${r}_outflow_per_active_user`,
            `, APPROX_QUANTILES(${r}_balance, 100)[OFFSET(50)] AS median_${r}_balance_per_active_user`,
            `, APPROX_QUANTILES(${r}_balance, 100)[OFFSET(75)] AS p75_${r}_balance_per_active_user`,
            `, APPROX_QUANTILES(${r}_balance, 100)[OFFSET(90)] AS p90_${r}_balance_per_active_user`,
        ])
        .join('\n    ')}

  FROM resourcesAggBase
  GROUP BY ALL
)

, active_users AS
(
  SELECT
    ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , sum_daily_active_users  
  FROM (  
    SELECT 
      ${resourcesSQL.cohortSplitGroupBy}${resourcesSQL.dateTrunc()} AS date
      , SUM(daily_active_users) AS sum_daily_active_users
    FROM
    (
      SELECT 
        ${
            usersdashActivitySQL.splitGroupBy != ''
                ? `${usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )}`
                : ''
        }date
        , COUNT(DISTINCT player_id) AS daily_active_users
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          usersdashActivity.sqlMap.dataset,
          usersdashActivity.sqlMap.tableId
      )}\` ds
          ${usersdashActivitySQL.join}
          ${usersdashActivitySQL.where}  
      GROUP BY ${resourcesSQL.cohortSplitGroupBy}date 
    )
    GROUP BY ${resourcesSQL.cohortSplitGroupBy}date 
  )
  ${
      // if datasource specific columns are selected, then we need to add them to the active users query
      resourcesSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${resourcesSQL.datasourceSpecificSplitGroupAlias} FROM resourcesAggBase)`
          : ''
  }
)

SELECT
${
    resourcesSQL.splitGroupBy !== ''
        ? `resourcesAgg.split_group AS split_group, `
        : ''
}resourcesAgg.date AS date
  , active_users.sum_daily_active_users
  ${allResources
      .flatMap((r) => [
          `, resourcesAgg.${r}_inflow`,
          `, resourcesAgg.${r}_outflow`,
          `, resourcesAgg.${r}_balance`,
      ])
      .join('\n  ')}

  ${allResources
      .flatMap((r) => [
          `, resourcesAgg.median_${r}_inflow_per_active_user`,
          `, resourcesAgg.p75_${r}_inflow_per_active_user`,
          `, resourcesAgg.p90_${r}_inflow_per_active_user`,
          `, resourcesAgg.median_${r}_outflow_per_active_user`,
          `, resourcesAgg.p75_${r}_outflow_per_active_user`,
          `, resourcesAgg.p90_${r}_outflow_per_active_user`,
          `, resourcesAgg.median_${r}_balance_per_active_user`,
          `, resourcesAgg.p75_${r}_balance_per_active_user`,
          `, resourcesAgg.p90_${r}_balance_per_active_user`,
      ])
      .join('\n  ')}


  ${allResources
      .flatMap((r) => [
          `, ROUND(SAFE_DIVIDE(resourcesAgg.${r}_inflow, sum_daily_active_users), 2) AS avg_${r}_inflow_per_active_user`,
          `, ROUND(SAFE_DIVIDE(resourcesAgg.${r}_outflow, sum_daily_active_users), 2) AS avg_${r}_outflow_per_active_user`,
          `, ROUND(SAFE_DIVIDE(resourcesAgg.${r}_balance, sum_daily_active_users), 2) AS avg_${r}_balance_per_active_user`,
      ])
      .join('\n  ')}

FROM resourcesAgg
LEFT JOIN active_users
ON resourcesAgg.date = active_users.date
${
    resourcesSQL.splitGroupBy !== ''
        ? `AND resourcesAgg.split_group = active_users.split_group`
        : ''
}
ORDER BY ${
                    resourcesSQL.splitGroupBy !== ''
                        ? `resourcesAgg.split_group, `
                        : ''
                }resourcesAgg.date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
